/**
 * File Upload Dialog — Triggered from paperclip button in chat input
 *
 * Features:
 * - Drag-and-drop zone
 * - Browse button
 * - File type validation
 * - File list with remove option
 * - Attach button to add files to the message
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload, X, FileText, File, Image as ImageIcon,
  FileSpreadsheet, FileCode,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

// PR-AAU-B (Ask Argus Pass 1) — the composer now has a single unified
// `Attach` button (paperclip). The dialog's `file` mode therefore accepts
// BOTH document and image MIME types — auto-routing by file type happens
// inside the uploader (text → ingestion, image → vision pipeline). The
// older `image` mode is preserved for callers that still want the
// image-only experience (e.g. Connections-side document uploads scoped
// to image-only configurations).
const ACCEPTED_DOCUMENT_TYPES = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'text/csv': 'CSV',
  'text/plain': 'TXT',
  'text/markdown': 'Markdown',
  'application/json': 'JSON',
  'application/yaml': 'YAML',
};

const ACCEPTED_IMAGE_TYPES = {
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
  'image/svg+xml': 'SVG',
};

const ACCEPTED_FILE_TYPES = {
  ...ACCEPTED_DOCUMENT_TYPES,
  ...ACCEPTED_IMAGE_TYPES,
};

interface FileUploadDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'file' | 'image';
  onAttach?: (files: File[]) => void;
}

interface SelectedFile {
  file: File;
  preview?: string;
}

export function FileUploadDialog({ open, onClose, mode, onAttach }: FileUploadDialogProps) {
  const [files, setFiles] = useState<SelectedFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      files.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    };
  }, [files]);

  const acceptedTypes = mode === 'image' ? ACCEPTED_IMAGE_TYPES : ACCEPTED_FILE_TYPES;
  const acceptString = Object.keys(acceptedTypes).join(',');

  const handleFiles = useCallback((fileList: FileList | null) => {
    if (!fileList) return;
    // #751 — reject unsupported MIME types BEFORE pushing into state.
    // The pre-fix code blindly accepted every dropped file regardless
    // of MIME, so drag/dropping a .exe, .zip, or any disallowed type
    // landed in `files` and looked attachable. The browser's `accept`
    // attribute filters the file picker but NOT drag-and-drop, so we
    // re-filter here using the same `acceptedTypes` map that drives
    // the picker.
    const newFiles: SelectedFile[] = [];
    const rejected: string[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (!(file.type in acceptedTypes)) {
        rejected.push(file.name);
        continue;
      }
      const sf: SelectedFile = { file };
      // Generate previews for ANY image (both modes) — the unified
      // `file` mode now accepts images too, and users expect to see a
      // thumbnail when they attach a PNG.
      if (file.type.startsWith('image/')) {
        sf.preview = URL.createObjectURL(file);
      }
      newFiles.push(sf);
    }
    if (rejected.length > 0) {
      toast.error(
        `Unsupported file type${rejected.length === 1 ? '' : 's'}: ${rejected.join(
          ', ',
        )}`,
      );
    }
    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles]);
    }
  }, [acceptedTypes]);

  const removeFile = (index: number) => {
    setFiles(prev => {
      const next = [...prev];
      if (next[index].preview) URL.revokeObjectURL(next[index].preview!);
      next.splice(index, 1);
      return next;
    });
  };

  const handleAttach = () => {
    onAttach?.(files.map(f => f.file));
    setFiles([]);
    onClose();
  };

  const handleClose = () => {
    files.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setFiles([]);
    onClose();
  };

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return ImageIcon;
    if (type.includes('spreadsheet') || type.includes('csv')) return FileSpreadsheet;
    if (type.includes('json') || type.includes('yaml') || type.includes('markdown')) return FileCode;
    if (type.includes('pdf') || type.includes('word')) return FileText;
    return File;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'image' ? 'Upload images' : 'Attach files or images'}</DialogTitle>
        </DialogHeader>

        {/* Drop zone */}
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
            dragging ? 'border-primary bg-primary/5' : 'border-border/50 hover:border-primary/30'
          )}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          // #751 — drag-and-drop must go through handleFiles which now
          // filters against acceptedTypes; the browser's `accept` attr
          // only filters the picker, not drops.
          onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        >
          <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm font-medium">Drop files here or click to browse</p>
          <p className="text-xs text-muted-foreground mt-1">
            {mode === 'image'
              ? 'PNG, JPEG, GIF, WebP, SVG'
              : 'PDF, DOCX, XLSX, CSV, TXT, Markdown, JSON, PNG, JPEG, GIF, WebP, SVG'}
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={acceptString}
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {files.map((sf, i) => {
              const Icon = getFileIcon(sf.file.type);
              return (
                <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/30">
                  {sf.preview ? (
                    <img src={sf.preview} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{sf.file.name}</p>
                    <p className="text-[10px] text-muted-foreground">{formatSize(sf.file.size)}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => removeFile(i)} aria-label={`Remove file '${sf.file.name}'`} title="Remove">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleAttach} disabled={files.length === 0}>
            Attach {files.length > 0 ? `(${files.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
